import React, { Component } from 'react';
import {
  Button,
  Select,
  Tag,
  Alert,
  Row,
  Col,
  Input,
  InputNumber,
  Steps,
  Switch,
  Checkbox,
  message,
} from 'antd';
import moment from 'moment';
import {
  verifyDoctorCenterMapping,
  bulkCreateDoctorSlots,
} from '../../services';
import { dayOptions, timeSlots } from './DoctorSlots';

const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;

const PURPLE = '#7651da';

const sectionStyle = {
  background: '#ffffff',
  border: '1px solid #e7e7e7',
  borderRadius: 6,
  padding: 18,
  marginBottom: 16,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

const sectionTitleStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: '#514f54',
  marginBottom: 4,
};

const sectionHelpStyle = {
  fontSize: 12,
  color: '#7c7c7c',
  marginBottom: 14,
};

const emptyRow = () => ({
  dow: [],
  slotType: 'fixed',
  startTime: '09:00:00',
  endTime: '13:00:00',
});

const defaultCenterConfig = () => ({
  appointmentType: 'prior_appointment',
  onCall: false,
  showSameDaySlots: false,
  leadTime: '2',
  postWorkingHour: '',
});

class DoctorSlotsBulkAdd extends Component {
  constructor(props) {
    super(props);
    this.state = {
      idsInput: '',
      parsedIds: [],
      verifying: false,
      verifyResults: null,
      slotRows: [emptyRow()],
      centerConfig: defaultCenterConfig(),
      submitting: false,
      submitResult: null,
    };
  }

  reset = () => {
    this.setState({
      idsInput: '',
      parsedIds: [],
      verifying: false,
      verifyResults: null,
      slotRows: [emptyRow()],
      centerConfig: defaultCenterConfig(),
      submitting: false,
      submitResult: null,
    });
  };

  setCenterConfig = (patch) => {
    this.setState((prev) => ({
      centerConfig: Object.assign({}, prev.centerConfig, patch),
    }));
  };

  parseIds = (raw) => {
    const seen = new Set();
    return raw
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isInteger(n) && n > 0)
      .filter((n) => {
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      });
  };

  onIdsInputChange = (e) => {
    const value = e.target.value;
    this.setState({
      idsInput: value,
      parsedIds: this.parseIds(value),
      verifyResults: null,
      submitResult: null,
    });
  };

  removeParsedId = (id) => {
    this.setState((prev) => {
      const remaining = prev.parsedIds.filter((n) => n !== id);
      return {
        parsedIds: remaining,
        idsInput: remaining.join(', '),
        verifyResults: null,
        submitResult: null,
      };
    });
  };

  verify = () => {
    const { parsedIds } = this.state;
    if (parsedIds.length === 0) {
      message.warning('Enter at least one center ID.');
      return;
    }
    this.setState({ verifying: true, verifyResults: null, submitResult: null });
    verifyDoctorCenterMapping(this.props.doctorId, parsedIds)
      .then((res) => {
        this.setState({ verifyResults: res.results || [] });
      })
      .catch(() => {
        message.error('Verification failed. Try again.');
      })
      .finally(() => this.setState({ verifying: false }));
  };

  currentStep = () => {
    const { verifyResults, submitResult } = this.state;
    if (submitResult) return 3;
    if (verifyResults) {
      const mapped = verifyResults.filter((r) => r.isMapped).length;
      return mapped > 0 ? 2 : 1;
    }
    return 0;
  };

  updateSlotRow = (idx, patch) => {
    this.setState((prev) => {
      const next = prev.slotRows.slice();
      next[idx] = Object.assign({}, next[idx], patch);
      return { slotRows: next };
    });
  };

  addSlotRow = () => {
    this.setState((prev) => ({ slotRows: [...prev.slotRows, emptyRow()] }));
  };

  removeSlotRow = (idx) => {
    this.setState((prev) => ({
      slotRows: prev.slotRows.filter((_, i) => i !== idx),
    }));
  };

  validateSlots = () => {
    const { slotRows } = this.state;
    if (slotRows.length === 0) return 'Add at least one slot row.';
    for (let i = 0; i < slotRows.length; i += 1) {
      const r = slotRows[i];
      if (!r.dow || r.dow.length === 0) return `Row ${i + 1}: pick at least one day.`;
      if (!r.startTime || !r.endTime) return `Row ${i + 1}: start/end time required.`;
      if (moment(r.endTime, 'HH:mm:ss').isSameOrBefore(moment(r.startTime, 'HH:mm:ss'))) {
        return `Row ${i + 1}: end time must be after start time.`;
      }
    }
    return null;
  };

  submit = () => {
    const err = this.validateSlots();
    if (err) {
      message.warning(err);
      return;
    }
    const mappedIds = (this.state.verifyResults || [])
      .filter((r) => r.isMapped)
      .map((r) => r.centerId);
    if (mappedIds.length === 0) {
      message.warning('No mapped centers to apply slots to.');
      return;
    }
    const { centerConfig } = this.state;
    if (
      centerConfig.postWorkingHour !== '' &&
      centerConfig.postWorkingHour !== null &&
      centerConfig.postWorkingHour !== undefined &&
      Number.isNaN(Number(centerConfig.postWorkingHour))
    ) {
      message.warning('Show Slots Post Working hours must be a number.');
      return;
    }
    const expandedSlots = [];
    this.state.slotRows.forEach((r) => {
      r.dow.forEach((d) => {
        expandedSlots.push({
          dow: Number(d),
          startTime: r.startTime,
          endTime: r.endTime,
          slotType: r.slotType,
        });
      });
    });
    const slotsConfig = {
      appointmentType: centerConfig.appointmentType,
      onCall: centerConfig.onCall ? '1' : '0',
      showSameDaySlot: centerConfig.showSameDaySlots ? 1 : 0,
      leadTime: centerConfig.showSameDaySlots
        ? Number(centerConfig.leadTime) || 0
        : 0,
      postWorkingHour:
        centerConfig.postWorkingHour === '' ||
        centerConfig.postWorkingHour === null ||
        centerConfig.postWorkingHour === undefined
          ? null
          : Number(centerConfig.postWorkingHour),
    };
    this.setState({ submitting: true, submitResult: null });
    bulkCreateDoctorSlots(this.props.doctorId, mappedIds, expandedSlots, true, slotsConfig)
      .then((res) => {
        this.setState({ submitResult: res });
        if (this.props.onSuccess) this.props.onSuccess(res);
      })
      .catch((err2) => {
        const data = err2 && err2.response && err2.response.data;
        const serverMsg =
          (data && data.err && Object.values(data.err)[0] && Object.values(data.err)[0].msg) ||
          (data && data.message && data.message !== 'Invalid fields' && data.message) ||
          'Bulk slot creation failed.';
        message.error(serverMsg);
      })
      .finally(() => this.setState({ submitting: false }));
  };

  renderSelectSection() {
    const { idsInput, parsedIds, verifying } = this.state;
    return (
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>1. Enter center IDs</div>
        <div style={sectionHelpStyle}>
          Paste comma-separated center IDs (e.g. <code>123, 456, 789</code>). Spaces and
          new lines are also accepted. Duplicates and non-numeric values are ignored.
        </div>
        <TextArea
          rows={3}
          value={idsInput}
          onChange={this.onIdsInputChange}
          placeholder="123, 456, 789"
          style={{ fontFamily: 'monospace' }}
        />
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#7c7c7c', marginBottom: 6 }}>
            {parsedIds.length} valid ID{parsedIds.length === 1 ? '' : 's'} parsed
          </div>
          <div>
            {parsedIds.map((id) => (
              <Tag
                key={id}
                closable
                onClose={() => this.removeParsedId(id)}
                style={{ marginBottom: 6 }}
              >
                #{id}
              </Tag>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            loading={verifying}
            disabled={parsedIds.length === 0}
            onClick={this.verify}
            style={{ background: PURPLE, borderColor: PURPLE }}
          >
            Verify mapping
          </Button>
          {parsedIds.length > 0 && (
            <Button onClick={this.reset}>Reset</Button>
          )}
        </div>
      </div>
    );
  }

  renderVerifySection() {
    const { verifyResults } = this.state;
    if (!verifyResults) return null;
    const mapped = verifyResults.filter((r) => r.isMapped);
    const unmapped = verifyResults.filter((r) => !r.isMapped);
    return (
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>2. Verification result</div>
        <div style={sectionHelpStyle}>
          Slots will only be created for centers mapped to this doctor.
        </div>
        {mapped.length === 0 && (
          <Alert
            type="error"
            showIcon
            message="No selected centers are mapped to this doctor"
            description="Map at least one center to this doctor first, then verify again."
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#389e0d' }}>
              Mapped ({mapped.length})
            </div>
            <div
              style={{
                maxHeight: 360,
                overflowY: 'auto',
                paddingRight: 6,
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                padding: 8,
                background: '#fafafa',
              }}
            >
              {mapped.length === 0 ? (
                <div style={{ color: '#9b9b9b', fontSize: 13 }}>None</div>
              ) : (
                mapped.map((r) => (
                  <div
                    key={r.centerId}
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #b7eb8f',
                      background: '#f6ffed',
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {r.centerName || `Center #${r.centerId}`}{' '}
                      <span style={{ color: '#9b9b9b', fontWeight: 400, fontSize: 12 }}>
                        #{r.centerId}
                      </span>
                    </div>
                    {r.locality && (
                      <div style={{ fontSize: 12, color: '#7c7c7c' }}>{r.locality}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#cf1322' }}>
              Not mapped ({unmapped.length})
            </div>
            <div
              style={{
                maxHeight: 360,
                overflowY: 'auto',
                paddingRight: 6,
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                padding: 8,
                background: '#fafafa',
              }}
            >
              {unmapped.length === 0 ? (
                <div style={{ color: '#9b9b9b', fontSize: 13 }}>None</div>
              ) : (
                unmapped.map((r) => (
                  <div
                    key={r.centerId}
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #ffa39e',
                      background: '#fff1f0',
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {r.centerName || `Center #${r.centerId}`}{' '}
                      <span style={{ color: '#9b9b9b', fontWeight: 400, fontSize: 12 }}>
                        #{r.centerId}
                      </span>
                    </div>
                    {r.locality && (
                      <div style={{ fontSize: 12, color: '#7c7c7c' }}>{r.locality}</div>
                    )}
                    {r.error && (
                      <div style={{ fontSize: 12, color: '#cf1322' }}>{r.error}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  renderConfigureSection() {
    const { slotRows, verifyResults, submitting, centerConfig } = this.state;
    if (!verifyResults) return null;
    const mappedCount = verifyResults.filter((r) => r.isMapped).length;
    if (mappedCount === 0) return null;
    return (
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>3. Configure slots</div>
        <div style={sectionHelpStyle}>
          These settings and slot rows will be applied to all {mappedCount} mapped
          center{mappedCount === 1 ? '' : 's'}.
        </div>
        <div
          style={{
            padding: 12,
            border: '1px solid #d9c8ff',
            background: '#f6f3ff',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 10, color: '#514f54' }}>
            Center defaults (applied to every center)
          </div>
          <Row gutter={[16, 12]} type="flex" align="middle">
            <Col xs={24} md={8}>
              <div style={{ fontSize: 12, color: '#7c7c7c', marginBottom: 4 }}>
                Appointment Type
              </div>
              <Select
                value={centerConfig.appointmentType}
                onChange={(v) => this.setCenterConfig({ appointmentType: v })}
                style={{ width: '100%' }}
              >
                <Option value="prior_appointment">Prior Appointment</Option>
                <Option value="walk_in">Walk In</Option>
              </Select>
            </Col>
            <Col xs={24} md={4}>
              <Checkbox
                checked={centerConfig.onCall}
                onChange={(e) => this.setCenterConfig({ onCall: e.target.checked })}
              >
                On Call
              </Checkbox>
            </Col>
            <Col xs={24} md={6}>
              <div style={{ fontSize: 12, color: '#7c7c7c', marginBottom: 4 }}>
                Same Day Slots
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  checked={centerConfig.showSameDaySlots}
                  onChange={(v) => this.setCenterConfig({ showSameDaySlots: v })}
                />
                <Select
                  value={centerConfig.leadTime}
                  onChange={(v) => this.setCenterConfig({ leadTime: v })}
                  disabled={!centerConfig.showSameDaySlots}
                  style={{ width: 110 }}
                >
                  <Option value="2">2 Hours</Option>
                  <Option value="4">4 Hours</Option>
                  <Option value="6">6 Hours</Option>
                </Select>
              </div>
            </Col>
            <Col xs={24} md={6}>
              <div style={{ fontSize: 12, color: '#7c7c7c', marginBottom: 4 }}>
                Show Slots Post Working hours
              </div>
              <InputNumber
                value={centerConfig.postWorkingHour}
                onChange={(v) => this.setCenterConfig({ postWorkingHour: v })}
                style={{ width: '100%' }}
                placeholder="Input a number"
                min={0}
              />
            </Col>
          </Row>
        </div>
        {slotRows.map((row, idx) => (
          <Row
            key={idx}
            gutter={8}
            style={{
              padding: 10,
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              marginBottom: 8,
              background: '#fafafa',
            }}
          >
            <Col span={7}>
              <div style={{ fontSize: 12, color: '#7c7c7c', marginBottom: 4 }}>Days</div>
              <Select
                mode="multiple"
                value={row.dow}
                onChange={(val) => this.updateSlotRow(idx, { dow: val })}
                style={{ width: '100%' }}
                placeholder="Pick days"
              >
                {dayOptions.map((d) => (
                  <Option key={d.value} value={d.value}>
                    {d.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={5}>
              <div style={{ fontSize: 12, color: '#7c7c7c', marginBottom: 4 }}>Type</div>
              <Select
                value={row.slotType}
                onChange={(val) => this.updateSlotRow(idx, { slotType: val })}
                style={{ width: '100%' }}
              >
                <Option value="fixed">Fixed</Option>
                <Option value="walk-in">Walk-in</Option>
              </Select>
            </Col>
            <Col span={5}>
              <div style={{ fontSize: 12, color: '#7c7c7c', marginBottom: 4 }}>Start</div>
              <Select
                showSearch
                value={row.startTime}
                onChange={(val) => this.updateSlotRow(idx, { startTime: val })}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {timeSlots.map((t) => (
                  <Option key={t.value} value={t.value}>
                    {t.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={5}>
              <div style={{ fontSize: 12, color: '#7c7c7c', marginBottom: 4 }}>End</div>
              <Select
                showSearch
                value={row.endTime}
                onChange={(val) => this.updateSlotRow(idx, { endTime: val })}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {timeSlots.map((t) => (
                  <Option key={t.value} value={t.value}>
                    {t.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                type="danger"
                icon="delete"
                shape="circle"
                disabled={slotRows.length === 1}
                onClick={() => this.removeSlotRow(idx)}
              />
            </Col>
          </Row>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button icon="plus" onClick={this.addSlotRow}>
            Add another row
          </Button>
          <Button
            type="primary"
            loading={submitting}
            onClick={this.submit}
            style={{ background: PURPLE, borderColor: PURPLE }}
          >
            Apply to {mappedCount} center{mappedCount === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    );
  }

  renderResultSection() {
    const { submitResult } = this.state;
    if (!submitResult) return null;
    const summary = submitResult.summary || {};
    const results = submitResult.results || [];
    return (
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>4. Result</div>
        <Alert
          type="success"
          showIcon
          message={`Created ${summary.totalSlotsCreated || 0} slot(s) across ${summary.totalCenters || 0} center(s).`}
          description={
            summary.totalSlotsSkipped
              ? `${summary.totalSlotsSkipped} skipped (duplicates), ${summary.totalSlotsFailed || 0} failed.`
              : `${summary.totalSlotsFailed || 0} failed.`
          }
          style={{ marginBottom: 12 }}
        />
        <div
          style={{
            maxHeight: 360,
            overflowY: 'auto',
            paddingRight: 6,
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            padding: 8,
            background: '#fafafa',
          }}
        >
          {results.map((r) => (
            <div
              key={r.centerId}
              style={{
                padding: '8px 10px',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                marginBottom: 6,
                background:
                  r.status === 'ok'
                    ? '#f6ffed'
                    : r.status === 'failed'
                    ? '#fff1f0'
                    : '#fffbe6',
              }}
            >
              <div style={{ fontWeight: 500 }}>
                {r.centerName || `Center #${r.centerId}`}{' '}
                <span style={{ color: '#9b9b9b', fontWeight: 400, fontSize: 12 }}>
                  #{r.centerId}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#7c7c7c' }}>
                created: {r.created || 0} · skipped: {r.skipped || 0} · failed: {r.failed || 0}
              </div>
            </div>
          ))}
        </div>
        <Button onClick={this.reset} style={{ marginTop: 8 }}>
          Start another bulk add
        </Button>
      </div>
    );
  }

  render() {
    const step = this.currentStep();
    return (
      <div>
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e7e7e7',
            borderRadius: 6,
            padding: '18px 24px',
            marginBottom: 16,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <Steps current={step} size="small">
            <Step title="Enter IDs" />
            <Step title="Verify" />
            <Step title="Configure" />
            <Step title="Result" />
          </Steps>
        </div>
        {this.renderSelectSection()}
        {this.renderVerifySection()}
        {this.renderConfigureSection()}
        {this.renderResultSection()}
      </div>
    );
  }
}

export default DoctorSlotsBulkAdd;
